/* eslint-disable react/prop-types */
import {
  Avatar,
  Button,
  Drawer,
  Flex,
  Form,
  Input,
  message,
  Space,
  Spin,
  Switch,
  Table,
  TableProps,
} from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SolutionGridVersion } from '../../../../types';
import { useSelector } from 'react-redux';
import {
  selectCurrentSolutionGridVersions,
  selectSolutionGridVersions,
} from '../../../../slices/solutionGrids';
import { ActionButtonWrapper } from './styles';
import Icon, { CloseOutlined, SaveOutlined } from '@ant-design/icons';
import { SVGEdit } from '../../../../images';
import { clone, isEqual } from 'lodash';
import { updateSolutionGridVersion } from '../../../../utils/httpRequest';
import { AvatarColors } from '../../../../constants';
import { getNameInitials } from '../../../../utils/formatter';

export interface VersionsDrawerProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  templateID: string;
}

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType?: 'string' | 'textarea';
  options?: any[];
  record: SolutionGridVersion;
  index: number;
}

const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}) => {
  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[
            {
              required: dataIndex === 'versionName',
              message: `Please Input ${title}!`,
            },
          ]}
        >
          {inputType === 'string' && <Input />}
          {inputType === 'textarea' && <Input.TextArea rows={1} />}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

const VersionsDrawer = ({
  open,
  onClose,
  onConfirm,
  templateID,
}: VersionsDrawerProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [versionID, setVersionID] = useState('');
  const [editingVersionId, setEditingVersionId] = useState('');

  const [loading, setLoading] = useState(false);

  const [versions, setVersions] = useState<SolutionGridVersion[]>([]);

  const originalVersions = useSelector(selectSolutionGridVersions);

  useEffect(() => {
    setVersions(originalVersions || []);
  }, [originalVersions]);

  const currentVersion = useSelector(selectCurrentSolutionGridVersions);

  const isUpdated =
    versionID !== currentVersion?.versionID ||
    !isEqual(versions, originalVersions);

  useEffect(() => {
    if (open) {
      setVersionID(currentVersion?.versionID || '');
    }
  }, [open]);

  const handleSaveVersion = (record: SolutionGridVersion) => {
    const nextVersions = clone(versions);
    let index = -1;
    const targetVersion = nextVersions.find((i, idx) => {
      if (i.versionID === record.versionID) {
        index = idx;
      }
      return i.versionID === record.versionID;
    });

    if (targetVersion) {
      nextVersions.splice(index, 1, {
        ...targetVersion,
        ...form.getFieldsValue(),
      });
      setVersions(nextVersions);
    }

    setEditingVersionId('');
  };

  const handleDelete = (record: SolutionGridVersion) => {
    const nextVersions = clone(versions);
    let index = -1;
    const targetVersion = nextVersions.find((i, idx) => {
      if (i.versionID === record.versionID) {
        index = idx;
      }
      return i.versionID === record.versionID;
    });

    if (targetVersion) {
      nextVersions.splice(index, 1, {
        ...targetVersion,
        effective: false,
      });
      setVersions(nextVersions);
    }

    nextVersions.splice(index, 1);
    setVersions(nextVersions);
  };

  const handleConfirm = async () => {
    try {
      setLoading(true);
      await updateSolutionGridVersion(
        templateID,
        versions.map((i) => ({
          ...i,
          effective: versionID === i.versionID,
        }))
      );
      onConfirm();
    } catch (err) {
      messageApi.error('Update failed, please try again.');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Effective',
      dataIndex: 'versionID',
      render: (value: any, record: SolutionGridVersion) => (
        <Switch
          checkedChildren="ON"
          unCheckedChildren="OFF"
          checked={versionID === value}
          onChange={(checked: boolean) =>
            checked ? setVersionID(value) : null
          }
        />
      ),
    },
    {
      title: 'Name',
      dataIndex: 'versionName',
      editable: true,
      inputType: 'string',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      width: 260,
      editable: true,
      inputType: 'textarea',
    },
    {
      title: 'Create By',
      dataIndex: 'submitterName',
      render: (value: string, record: SolutionGridVersion, index: number) => (
        <Space>
          <Avatar style={AvatarColors[index % AvatarColors.length]}>
            {getNameInitials(value)}
          </Avatar>
          {value}
        </Space>
      ),
    },
    {
      title: 'Latest updated',
      dataIndex: 'updateTime',
      render: (value: any, record: SolutionGridVersion) => {
        const time = record.updateTime || record.createTime;
        return time.split('T').join(' ');
      },
    },
    {
      title: 'Action',
      dataIndex: 'action',
      render: (value: any, record: SolutionGridVersion, index: number) => {
        const editable = record.versionID === editingVersionId;
        return editable ? (
          <Space>
            <ActionButtonWrapper
              style={{ cursor: 'pointer' }}
              title={t('save')}
              onClick={() => handleSaveVersion(record)}
            >
              <SaveOutlined />
            </ActionButtonWrapper>
            <ActionButtonWrapper
              style={{ cursor: 'pointer' }}
              title={t('cancel')}
              onClick={() => setEditingVersionId('')}
            >
              <CloseOutlined />
            </ActionButtonWrapper>
          </Space>
        ) : (
          <Flex>
            <ActionButtonWrapper
              style={{ cursor: 'pointer' }}
              title={t('edit')}
              onClick={() => {
                form.setFieldsValue(record);
                setEditingVersionId(record.versionID);
              }}
            >
              <Icon component={SVGEdit} />
            </ActionButtonWrapper>
            {/* <Popconfirm
              placement="bottom"
              title={`Are you sure you want to delete ${record.versionName}`}
              okText="Yes"
              cancelText="No"
              onConfirm={(e) => handleDelete(record)}
            >
              <ActionButtonWrapper
                style={{ cursor: 'pointer' }}
                title={t('delete')}
              >
                <Icon component={SVGDelete} />
              </ActionButtonWrapper>
            </Popconfirm> */}
          </Flex>
        );
      },
    },
  ];

  const mergedColumns: TableProps<SolutionGridVersion>['columns'] = columns.map(
    (col) => {
      if (!col.editable) {
        return col;
      }
      return {
        ...col,
        onCell: (record: SolutionGridVersion) => ({
          record,
          inputType: col.inputType,
          dataIndex: col.dataIndex,
          title: col.title,
          editing: record.versionID === editingVersionId,
        }),
      };
    }
  );

  return (
    <Drawer
      open={open}
      closable
      title="Edit Versions"
      onClose={onClose}
      width={960}
      footer={
        <Flex justify="flex-end" gap={16}>
          <Button type="primary" ghost onClick={onClose} disabled={!isUpdated}>
            {t('cancel')}
          </Button>
          <Button type="primary" onClick={handleConfirm} disabled={!isUpdated}>
            {t('confirm')}
          </Button>
        </Flex>
      }
    >
      {contextHolder}
      <Form form={form} component={false}>
        <Table
          columns={mergedColumns}
          dataSource={(versions || []).map((i) => ({
            key: i.versionID,
            ...i,
          }))}
          components={{
            body: { cell: EditableCell },
          }}
          pagination={false}
        ></Table>
      </Form>
      <Spin spinning={loading} fullscreen />
    </Drawer>
  );
};

export default VersionsDrawer;
